import { IntegrationAndProperties } from './../classes/integrationAndProperties';
import { IntegrationVm } from '../classes/integrationVm';
import { AppResponse } from 'modules/core/interfaces/appResponse';
import AuthorizedApiClient from 'modules/auth/models/authorizedApiClient';
import axios, { AxiosResponse, AxiosError } from 'axios';
import store from '@/store/index';
import { ContactProperty } from '../../installation-wizard/classes/contactProperty';
import { Integrations } from '../../installation-wizard/enums/integrations';
import environment from '@/environment';
import { IntegrationDefinitionVm } from '../classes/integrationDefinitionVm';
import { IntegrationApiKeys } from '@/modules/installation-wizard/classes/integrationApiKeys';
import appMessage from '@/modules/core/models/appMessage';

export class IntegrationApi {
  public MasterAccountId: string;

  public constructor() {
    this.MasterAccountId = store.state.auth && store.state.auth.user
    ? store.state.auth.user.MasterAccountId
    : null;
  }

  public async list(): Promise<IntegrationVm[]> {
    const response: AxiosResponse<
      AppResponse<IntegrationVm[]>
    > = await AuthorizedApiClient.get('/account/GetIntegrations');
    const appResponse: AppResponse<IntegrationVm[]> = response.data;
    if (appResponse.IsSuccess) {
      const devices: IntegrationVm[] = appResponse.Result ? appResponse.Result : [];
      return devices;
    } else {
      throw new Error(appResponse.ErrorCodes.join(','));
    }
  }

  public async getIntegrationAndProps(integrationId: string): Promise<IntegrationAndProperties> {
    const response: AxiosResponse<
      AppResponse<IntegrationAndProperties>
    > = await AuthorizedApiClient.get('/account/GetIntegration?id=' + integrationId);
    const appResponse: AppResponse<IntegrationAndProperties> = response.data;
    if (appResponse.IsSuccess) {
      const data: IntegrationAndProperties = appResponse.Result;
      return data;
    } else {
      throw new Error(appResponse.ErrorCodes.join(','));
    }
  }

  public async startDataSync(shopOrigin: string): Promise<boolean> {
    return axios.post(environment.backendUrl + '/shopify/api/v1.0/settings/RunSync', {},
    {
      headers: {
        'Content-Type': 'application/json',
        'x-lc-shoporigin': shopOrigin,
      },
    }).then((response: AxiosResponse<AppResponse<null>>) => {
      return true;
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<null> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async updateProps(props: ContactProperty[]) {
    const MasterAccountId = this.MasterAccountId;
    const response: AxiosResponse<
      AppResponse<string>
    > = await AuthorizedApiClient.post('/contact/ConfigureProperties', {
      MasterAccountId,
      ConfigItems: props,
    });

    const appResponse: AppResponse<string> = response.data;
    if (appResponse.IsSuccess) {
      return appResponse.Result;
    } else {
      throw new Error(appResponse.ErrorCodes.join(','));
    }
  }

  public async getApiKey(shopOrigin: string): Promise<string> {
    const macid = this.MasterAccountId;
    return axios.post(
      environment.backendUrl + '/shopify/api/v1.0/settings/GetApiKey',
      {
        shopUrl: shopOrigin,
        MasterAccountId: macid,
      },
    ).then((response: AxiosResponse<AppResponse<string>>) => {
      return response.data.Result;
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<string> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async getProps(integration: Integrations): Promise<ContactProperty[] | undefined> {
    return axios.get(environment.backendUrl + '/api/v1.0/creator/GetProperties?integration=' + integration,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response: AxiosResponse<AppResponse<ContactProperty[]>>) => {
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return responseData.Result;
        } else {
          throw new Error(responseData.ErrorCodes.join(','));
        }
      }
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<ContactProperty[]> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async getIntegrationsDefinitions(): Promise<IntegrationDefinitionVm[]> {
    return axios.get(environment.backendUrl + ('/api/v1.0/integration/list'))
      .then((response: AxiosResponse<AppResponse<IntegrationDefinitionVm[]>>) => {
      const appResponse: AppResponse<IntegrationDefinitionVm[]> = response.data;
      if (appResponse.IsSuccess) {
        const devices: IntegrationDefinitionVm[] = appResponse.Result ? appResponse.Result : [];
        return devices;
      } else {
        throw new Error(appResponse.ErrorCodes.join(','));
      }
    });
  }

  public async getIntegrationApiKeys(integration: string): Promise<IntegrationApiKeys> {
    const response: AxiosResponse<
      AppResponse<IntegrationApiKeys>
    > = await AuthorizedApiClient.get('/account/GetApiKeys?integration=' + integration);
    const appResponse: AppResponse<IntegrationApiKeys> = response.data;
    if (appResponse.IsSuccess) {
      const data: IntegrationApiKeys = appResponse.Result;
      return data;
    } else {
      throw new Error(appResponse.ErrorCodes.join(','));
    }
  }

  public async saveIntegrationApiKeys(data: IntegrationApiKeys): Promise<any> {
    return AuthorizedApiClient.post(environment.backendUrl + '/account/SaveApiKeys', data,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response: AxiosResponse<AppResponse<any>>) => {
      return response.data;
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<null> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }
  public async setIntegration(data: IntegrationVm) : Promise<boolean>
  {
    return AuthorizedApiClient.post(environment.backendUrl + '/account/SetIntegration', 
    {
      MasterAccountId: this.MasterAccountId,
      Type: data.type,
      Id: data.id,
      Status: data.status,
      Name: data.name
    }).then((response: AxiosResponse<AppResponse<any>>) => {
      return true;
    }).catch((error: AxiosError) =>{
      return false;
    })
  }

  public async deleteIntegration(data: IntegrationVm) : Promise<boolean> 
  {
    return AuthorizedApiClient.post(environment.backendUrl + '/account/DeleteIntegrationAndProps', {
      MasterAccountId: this.MasterAccountId,
      Integration: data.type,
      Domain: data.id,
      ApiKey: data.apikey
    }).then((response: AxiosResponse<AppResponse<any>>) => {
      return true;
    }).catch((error: AxiosError) => {
      return false;
    })
  }

  public async activateIntegration(data: IntegrationVm) : Promise<boolean> 
  {
    return AuthorizedApiClient.post(environment.backendUrl + '/account/ActivateIntegration', {
      MasterAccountId: this.MasterAccountId,
      Integration: data.type,
      Domain: data.id
    }).then((response: AxiosResponse<AppResponse<any>>) => {
      return true;
    }).catch((error: AxiosError) => {
      return false;
    })
  }
}

